/**
 * rc136 — "Instalar ByteISP en este dispositivo".
 *
 * · Android / Chrome / Edge: botón propio que dispara el prompt nativo
 *   (`beforeinstallprompt`, capturado en lib/push.ts).
 * · iPhone / iPad: Safari no ofrece prompt — guía de Compartir → Agregar a inicio.
 * · Ya instalada (standalone): lo dice y no estorba.
 */
import { useSyncExternalStore } from "react";
import { toast } from "sonner";
import { Smartphone, Share, Download, CheckCircle2, MoreVertical } from "lucide-react";
import { Button } from "@/components/ui/button";
import { esIOS, instalacionNativaDisponible, instalada, instalarApp, onInstalacionCambio } from "@/lib/push";

function useInstalacionNativa(): boolean {
  return useSyncExternalStore(onInstalacionCambio, instalacionNativaDisponible, () => false);
}

export function InstalarAppCard() {
  const nativa = useInstalacionNativa();
  const yaInstalada = instalada();
  const ios = esIOS();

  async function instalar() {
    const r = await instalarApp();
    if (r === "accepted") toast.success("ByteISP instalada — búscala en tu pantalla de inicio");
    else if (r === "dismissed") toast.info("Instalación cancelada", { description: "Puedes volver a intentarlo aquí cuando quieras." });
  }

  return (
    <div className="rounded-lg border bg-card p-4 space-y-3">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          {yaInstalada ? <CheckCircle2 size={16} className="text-green-500" /> : <Smartphone size={16} className="text-muted-foreground" />}
          <div>
            <div className="text-sm font-semibold">Instalar ByteISP en este dispositivo</div>
            <div className="text-[11px] text-muted-foreground">
              {yaInstalada
                ? "Ya estás usando ByteISP como app instalada."
                : "Como app: ícono en tu pantalla de inicio, pantalla completa y notificaciones."}
            </div>
          </div>
        </div>
        {!yaInstalada && nativa && (
          <Button size="sm" onClick={instalar}>
            <Download size={12} /> Instalar
          </Button>
        )}
      </div>

      {!yaInstalada && ios && (
        <div className="rounded border border-primary/30 bg-primary/5 p-3 text-xs text-muted-foreground">
          <div className="mb-1 flex items-center gap-1.5 font-medium text-foreground"><Smartphone size={13} /> En iPhone / iPad (Safari)</div>
          Toca <Share size={12} className="inline align-middle" /> <b>Compartir</b> → <b>Agregar a pantalla de inicio</b> → <b>Agregar</b>.
          Luego abre ByteISP desde el ícono.
        </div>
      )}

      {!yaInstalada && !ios && !nativa && (
        <div className="rounded border bg-muted/30 p-3 text-xs text-muted-foreground">
          <div className="mb-1 flex items-center gap-1.5 font-medium text-foreground"><MoreVertical size={13} /> Desde el menú del navegador</div>
          En Android (Chrome): menú <b>⋮</b> → <b>Instalar app</b> (o <b>Agregar a pantalla de inicio</b>).
          En computadora: el ícono de instalar en la barra de direcciones.
        </div>
      )}
    </div>
  );
}
